/**
 * Geodesic measurement for map annotations (ADR-034; feature-spec §6 / §9 /
 * D3 / D5). Pure functions over WGS84 coordinates + the operator's units —
 * line distance + **true** azimuth, circle radius, polygon area + perimeter,
 * marker coordinates. Reused by the drawing flow (live readout) and the
 * on-map readout layer (a later increment).
 *
 * Canonical inputs are WGS84 degrees + metres (turf does the geodesic math);
 * display strings are derived per the operator's `units.distance` /
 * `units.coordinate_format` via the existing `@/units` formatters. Azimuth is
 * TRUE north only (D5) — magnetic is deferred.
 *
 * Cross-references:
 *   - ADR-034; feature-spec §6 (shapes) / §9 (units) / D3 / D5 / D11 / D13.
 *   - @/units/format-distance / format-coordinate (display formatting).
 */
import area from "@turf/area";
import bearing from "@turf/bearing";
import distance from "@turf/distance";
import type { Polygon } from "geojson";
import type { CoordinateFormat, DistanceUnit } from "@/auth/session-types";
import { formatCoordinate } from "@/units/format-coordinate";
import { formatDistance } from "@/units/format-distance";

/** Em-dash placeholder for a non-finite magnitude (matches the unit formatters). */
const EM_DASH = "—";

/** Square-metres → squared distance unit, with the display suffix (D13).
 *  Divisors are the exact unit definitions squared (see format-distance.ts). */
const AREA_TABLE: Readonly<Record<DistanceUnit, { perSquareMetre: number; suffix: string }>> = {
  nautical_miles: { perSquareMetre: 1852 * 1852, suffix: "nm²" },
  kilometers: { perSquareMetre: 1_000_000, suffix: "km²" },
  statute_miles: { perSquareMetre: 1609.344 * 1609.344, suffix: "mi²" },
};

/** Normalize any degree value into ``[0, 360)``. */
function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Format a true azimuth as ``"NNN°T"`` — three digits, 0–359, true north (D5).
 * E.g. ``47`` → ``"047°T"``; ``-90`` → ``"270°T"``; ``359.7`` → ``"000°T"``.
 */
export function formatAzimuth(deg: number): string {
  if (!Number.isFinite(deg)) {
    return EM_DASH;
  }
  const normalized = normalizeDegrees(Math.round(deg)) % 360;
  return `${String(normalized).padStart(3, "0")}°T`;
}

/** Format a canonical m² area in the operator's squared distance unit (D13). */
export function formatArea(squareMeters: number, unit: DistanceUnit): string {
  if (!Number.isFinite(squareMeters)) {
    return EM_DASH;
  }
  const { perSquareMetre, suffix } = AREA_TABLE[unit];
  return `${(squareMeters / perSquareMetre).toFixed(1)} ${suffix}`;
}

/** Sum the geodesic length of consecutive segments (metres). */
function sumSegmentMetres(coords: readonly number[][]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i += 1) {
    total += distance(coords[i - 1], coords[i], { units: "meters" });
  }
  return total;
}

export interface LineMeasurement {
  /** Total geodesic distance over all segments (metres). */
  readonly distanceM: number;
  /** Start→end true azimuth, 0–359°. */
  readonly azimuthDeg: number;
  /** Display readout, e.g. ``"12.3 nm · 047°T"``. */
  readonly label: string;
}

/** Total geodesic distance + start→end true azimuth for a polyline (≥2 vertices, D11). */
export function lineMeasurement(coords: readonly number[][], unit: DistanceUnit): LineMeasurement {
  const distanceM = sumSegmentMetres(coords);
  const azimuthDeg = normalizeDegrees(bearing(coords[0], coords[coords.length - 1]));
  return {
    distanceM,
    azimuthDeg,
    label: `${formatDistance(distanceM, unit)} · ${formatAzimuth(azimuthDeg)}`,
  };
}

export interface CircleMeasurement {
  readonly radiusM: number;
  /** Display readout, e.g. ``"R 5.0 nm"``. */
  readonly label: string;
}

/** Circle radius readout. */
export function circleMeasurement(radiusM: number, unit: DistanceUnit): CircleMeasurement {
  return { radiusM, label: `R ${formatDistance(radiusM, unit)}` };
}

/** Circle area in m² (= πr²). The granular circle readouts (area / radius /
 *  circumference — Inc C) format these via formatArea / formatDistance. */
export function circleAreaM2(radiusM: number): number {
  return Math.PI * radiusM * radiusM;
}

/** Circle circumference in metres (= 2πr). */
export function circleCircumferenceM(radiusM: number): number {
  return 2 * Math.PI * radiusM;
}

export interface PolygonMeasurement {
  readonly areaM2: number;
  readonly perimeterM: number;
  /** Display readout, e.g. ``"4.2 km² · 8.1 km"``. */
  readonly label: string;
}

/**
 * Geodesic area + perimeter for a polygon's outer ring (closed, ≥4 positions).
 * Pass the outer ring coordinates.
 */
export function polygonMeasurement(
  ring: readonly number[][],
  unit: DistanceUnit,
): PolygonMeasurement {
  const polygon: Polygon = { type: "Polygon", coordinates: [ring.map((p) => [...p])] };
  const areaM2 = area(polygon);
  const perimeterM = sumSegmentMetres(ring);
  return {
    areaM2,
    perimeterM,
    label: `${formatArea(areaM2, unit)} · ${formatDistance(perimeterM, unit)}`,
  };
}

export interface MarkerMeasurement {
  /** The coordinate string in the operator's coordinate format. */
  readonly label: string;
}

/** The marker's coordinates in the operator's coordinate format. ``coord`` is
 *  ``[lon, lat]``. ``precision`` caps decimal-degree places (#8 — on-map readouts
 *  use 3, vs the formatter's 5-place default); DMS/MGRS ignore it. */
export function markerMeasurement(
  coord: readonly number[],
  format: CoordinateFormat,
  precision = 3,
): MarkerMeasurement {
  return { label: formatCoordinate(coord[1], coord[0], format, { precision }) };
}
